import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { preChartLabsSchema } from "./pre-chart";
import { publicIdSchema } from "./public-id";
import { visitSourceIdsSchema } from "./visit-source";

const id = publicIdSchema;
// Why the edit was asked for. "transcript-edit": the instruction was derived
// from the clinician's transcript correction, so every clinical fact change
// (even one the instruction names) needs the clinician's confirmation, and
// spoken shortcuts in the quoted transcript are never expanded.
export const noteEditOriginSchema = z.enum(["transcript-edit"]);
export type NoteEditOrigin = z.output<typeof noteEditOriginSchema>;
export const NOTE_EDIT_INSTRUCTION_MAX = 4000;
// A proposal can be refined before it is applied ("remove medication
// history", then "remove the heading too"): at most this many requests in
// one combined proposal, whose instructions together (one per line) stay
// within NOTE_EDIT_COMBINED_INSTRUCTION_MAX characters.
export const NOTE_EDIT_MAX_STEPS = 8;
export const NOTE_EDIT_COMBINED_INSTRUCTION_MAX = 32000;
const instructionSchema = z
  .string()
  .trim()
  .min(1)
  .max(NOTE_EDIT_INSTRUCTION_MAX);
const noteVersionSchema = z.string().regex(/^[0-9a-f]{64}$/);
// The proposal being refined: its text (not saved yet), the saved note
// version it was proposed on, and the requests it already contains. The
// server re-reads the saved note, refuses the refinement when that note
// changed (stale_proposal), and checks the combined result against the
// saved note and the transcript as for any proposal.
export const noteEditRefineSchema = z.strictObject({
  text: z
    .string()
    .max(60000)
    .refine((value) => value.trim().length > 0, "A draft cannot be empty"),
  expectedVersion: noteVersionSchema,
  instructions: z
    .array(instructionSchema)
    .min(1)
    .max(NOTE_EDIT_MAX_STEPS - 1),
});
export type NoteEditRefine = z.output<typeof noteEditRefineSchema>;
// The instruction a combined proposal is checked and applied with: every
// request in it, oldest first, one per line.
export const combinedNoteEditInstruction = (
  instructions: readonly string[],
): string => instructions.map((value) => value.trim()).join("\n");
export const noteEditProposalInputSchema = z
  .strictObject({
    instruction: instructionSchema,
    notePublicId: id.optional(),
    // Visit sources (uploaded labs, investigations, previous notes) the
    // assistant may use for this edit.
    sourceIds: visitSourceIdsSchema.optional(),
    origin: noteEditOriginSchema.optional(),
    refine: noteEditRefineSchema.optional(),
    // Pre-chart drafts: which lab values to take (ignored on other notes).
    preChartLabs: preChartLabsSchema.optional(),
    // Pre-chart drafts: how the attached files are added. "spread": under
    // the template's headings, an earlier consult note summarized under the
    // history heading; "summary": a short summary under one heading;
    // "labs": a lab report's values (preChartLabs: all, abnormal or the
    // picked tests) under one heading.
    preChartAdd: z.enum(["spread", "summary", "labs"]).optional(),
    // "summary" and "labs": the heading to write under, as the note names
    // it (else the history heading for a summary, the results heading for
    // labs).
    preChartHeading: z.string().trim().min(1).max(200).optional(),
  })
  .refine(
    (value) =>
      value.refine === undefined ||
      combinedNoteEditInstruction([
        ...value.refine.instructions,
        value.instruction,
      ]).length <= NOTE_EDIT_COMBINED_INSTRUCTION_MAX,
    "Combined instructions exceed the limit",
  )
  .refine(
    (value) => value.refine === undefined || value.origin === undefined,
    "A transcript correction cannot be refined",
  );
export const noteEditProposalConflictSchema = z.object({
  reason: z.literal("stale_proposal"),
});
export const noteEditProposalSchema = z.object({
  target: z.object({ visitPublicId: id, notePublicId: id.nullable() }),
  baseVersion: noteVersionSchema,
  source: z.enum(["ai", "macro"]),
  current: z.string().max(60000),
  proposed: z.string().min(1).max(60000),
  diff: z
    .array(
      z.object({
        op: z.enum(["equal", "insert", "delete"]),
        text: z.string(),
      }),
    )
    .max(4000),
  factChanges: z.array(
    z.object({
      fact: z.string(),
      kind: z.enum([
        "medication",
        "allergy",
        "allergy-reaction",
        "vital",
        "result",
        "follow-up",
        "duration",
        "negation",
        "laterality",
        "decision",
      ]),
      before: z.string().nullable(),
      after: z.string().nullable(),
      status: z.enum(["kept", "changed-by-instruction", "LOST", "ADDED"]),
      inTranscript: z.boolean().optional(),
      // For ADDED facts: also present in a selected visit source.
      inSource: z.boolean().optional(),
    }),
  ),
  blocked: z.boolean(),
  macros: z.object({
    inserted: z.array(
      z.object({
        trigger: z.string(),
        section: z.string(),
        text: z.string(),
      }),
    ),
    skipped: z.array(
      z.object({
        trigger: z.string(),
        reason: z.enum(["negated", "cancelled"]),
        spoken: z.string(),
      }),
    ),
    notApplied: z.string(),
  }),
});
export type NoteEditProposalBody = z.output<typeof noteEditProposalInputSchema>;
export const noteEditProposalContract = {
  createNoteEditProposal: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Note not found" },
      // Refining a proposal whose saved note changed since.
      CONFLICT: {
        message: "The note changed since this proposal",
        data: noteEditProposalConflictSchema,
      },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Note edit proposal unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({
          public_id: publicIdSchema,
        }),
        query: z.strictObject({}).default({}),
        body: noteEditProposalInputSchema,
      }),
    )
    .meta(
      openapi({
        method: "POST",
        path: "/api/v2/visits/{public_id}/note-edit-proposals",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(noteEditProposalSchema),
};
